// ===========================================
// Move an enquiry to another stage (enquiries plan §5.4, §5.7)
// ===========================================
// The server applies the seven rules; this dialog collects what each target needs and shows the
// server's reason inline. `expectedFromStage` is always the stage the drawer showed, so a move by
// someone else is a 409 rather than a silent overwrite. MNG Booked is never a plain transition:
// the Meet & Greet is created or linked first and the server moves the stage itself.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTenant } from '../../../contexts/TenantContext';
import type { Enquiry, EnquiryStage, TransitionEnquiryData } from '../../../api/enquiry.api';
import {
  apiErrorCode,
  apiErrorMessage,
  useAssignableOwners,
  useEnquiryConfig,
  useEnquiryOptions,
  useTransitionEnquiry,
} from '../../../hooks/useEnquiries';
import { usePermissions } from '../../../hooks/usePermissions';
import ConvertToMeetAndGreetDialog from '../ConvertToMeetAndGreetDialog';
import EnquiryOptionSelect from './EnquiryOptionSelect';
import LinkMeetAndGreetDialog from './LinkMeetAndGreetDialog';
import {
  BOOKING_MOVES_FROM,
  canMarkEnrolled,
  isDeadBooking,
  manualTargets,
  mngTeacherOption,
  stageLabel,
  stageView,
} from './enquiryUi';

export interface MoveStageDialogProps {
  enquiry: Enquiry;
  open: boolean;
  onClose: () => void;
  initialStage?: EnquiryStage;
}

const STAGE_DEFAULT = '';

export default function MoveStageDialog({ enquiry, open, onClose, initialStage }: MoveStageDialogProps) {
  const { can } = usePermissions();
  const { schoolSlug } = useTenant();
  const { data: config } = useEnquiryConfig();
  const { data: owners } = useAssignableOwners(open);
  const { data: closedReasons } = useEnquiryOptions('CLOSED_REASON', { forNewEnquiry: true }, open);
  const transition = useTransitionEnquiry({ quietErrors: true });

  const [target, setTarget] = useState<EnquiryStage | ''>('');
  const [ownerId, setOwnerId] = useState<string>(STAGE_DEFAULT);
  const [nextAction, setNextAction] = useState('');
  const [due, setDue] = useState('');
  const [closedReasonId, setClosedReasonId] = useState('');
  const [closedDetail, setClosedDetail] = useState('');
  const [program, setProgram] = useState('');
  const [preferredDay, setPreferredDay] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Phase 6: the move was refused for an unfinished onboarding checklist (the error says whose).
  const [checklistBlocked, setChecklistBlocked] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  // After a Create or Link: wait for the refetched enquiry (its booking changes), then close if the
  // stage really moved, else say the booking is recorded (review 5.7 L12).
  const [awaitingRefetch, setAwaitingRefetch] = useState(false);
  const [bookingRecorded, setBookingRecorded] = useState(false);
  const mngIdAtBooking = useRef<string | null>(null);

  // Start over each time the dialog opens (or is opened for another enquiry).
  useEffect(() => {
    if (!open) return;
    setTarget(initialStage ?? '');
    setClosedReasonId('');
    setClosedDetail('');
    setProgram('');
    setPreferredDay('');
    setNote('');
    setError(null);
    setChecklistBlocked(false);
    setCreateOpen(false);
    setLinkOpen(false);
    setAwaitingRefetch(false);
    setBookingRecorded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, enquiry.id, initialStage]);

  const targets = useMemo(() => {
    const list = manualTargets(config, enquiry.stage);
    return list.filter((s) => s.stage !== 'ENROLLED' || canMarkEnrolled(config, enquiry.stage));
  }, [config, enquiry.stage]);

  // A target that drops out of the list (a 409 refetch moved the enquiry onto it, or the stage was
  // switched off) is cleared, so it can never be submitted (review 5.7 L13).
  const targetValid = !!target && targets.some((s) => s.stage === target);
  useEffect(() => {
    if (target && targets.length > 0 && !targetValid) setTarget('');
  }, [target, targets, targetValid]);

  const view = target ? stageView(config, target) : undefined;
  const isOwned = !!view?.isOwned;
  // An MNG_TEACHER stage defaults to the Meet & Greet's teacher, who is not in the owner picker
  // (review 5.7 M7).
  const mngTeacher = view?.owner?.target.kind === 'MNG_TEACHER' ? mngTeacherOption(enquiry) : null;

  // Re-fill the owner, action and due day from the stage's defaults whenever the target changes.
  const defaultsKey = view
    ? `${view.stage}|${mngTeacher?.id ?? view.owner?.userId ?? ''}|${view.defaults?.nextAction ?? ''}|${view.defaults?.nextActionDueDate ?? ''}`
    : '';
  useEffect(() => {
    if (!view?.isOwned) return;
    setOwnerId(mngTeacher?.id ?? view.owner?.userId ?? STAGE_DEFAULT);
    setNextAction(view.defaults?.nextAction ?? '');
    setDue(view.defaults?.nextActionDueDate ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultsKey]);

  const ownerList = Array.isArray(owners) ? owners : [];
  const ownerOptions: Array<{ id: string; name: string }> = ownerList.map((o) => ({
    id: o.id,
    name: `${o.firstName} ${o.lastName}`,
  }));
  if (mngTeacher && !ownerOptions.some((o) => o.id === mngTeacher.id)) {
    ownerOptions.push({ id: mngTeacher.id, name: `${mngTeacher.name} (Meet & Greet teacher)` });
  }
  const reasonList = Array.isArray(closedReasons) ? closedReasons : [];
  const chosenReason = reasonList.find((r) => r.id === closedReasonId);
  const needsDetail = chosenReason?.systemKey === 'OTHER';
  const guardrail = !!config?.settings.requireNextActionOnStageChange;
  const minDue = config?.schoolToday;
  const duePast = !!due && !!minDue && due < minDue;

  // MNG Booked: the Meet & Greet is made or linked first (A1); never a plain transition. A cancelled
  // or rejected booking counts as none; an unconfirmed one can be replaced (the server's rule, §8.2).
  const mng = enquiry.MeetAndGreet;
  const mngDead = !!mng && isDeadBooking(mng.status);
  const wantsMng = target === 'MNG_BOOKED';
  const needsMngFirst = wantsMng && (!mng || mngDead);
  const mngUnverified = wantsMng && mng?.status === 'PENDING_VERIFICATION';
  const mngBlockedReason =
    wantsMng && mng?.status === 'CONVERTED'
      ? 'This Meet & Greet has already become a family, so the enquiry cannot be moved to MNG Booked.'
      : wantsMng && mng?.status === 'WAITLISTED'
        ? 'This Meet & Greet is on the waitlist, so the enquiry cannot be moved to MNG Booked until it is off it.'
        : null;
  const mayBookMng = can('meet_and_greet', 'write');
  const bookingMovesStage = BOOKING_MOVES_FROM.includes(enquiry.stage);

  // The refetched enquiry carries the new booking: only then is the stage known.
  useEffect(() => {
    if (!awaitingRefetch) return;
    if ((enquiry.MeetAndGreet?.id ?? null) === mngIdAtBooking.current) return;
    setAwaitingRefetch(false);
    if (enquiry.stage === 'MNG_BOOKED') onClose();
    else setBookingRecorded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingRefetch, enquiry.MeetAndGreet?.id, enquiry.stage]);

  const openBooking = (which: 'create' | 'link') => {
    mngIdAtBooking.current = enquiry.MeetAndGreet?.id ?? null;
    if (which === 'create') setCreateOpen(true);
    else setLinkOpen(true);
  };

  const handleBooked = () => {
    setCreateOpen(false);
    setLinkOpen(false);
    setAwaitingRefetch(true);
  };

  const missing: string[] = [];
  if (!target) missing.push('stage');
  if (isOwned && guardrail) {
    if (!ownerId) missing.push('owner');
    if (!nextAction.trim()) missing.push('next action');
    if (!due) missing.push('due date');
  }
  if (duePast && !missing.includes('due date')) missing.push('due date');
  if (target === 'NOT_PROCEEDING') {
    if (!closedReasonId) missing.push('reason');
    else if (needsDetail && !closedDetail.trim()) missing.push('detail');
  }
  if (target === 'MNG_FOLLOW_UP' && !note.trim()) missing.push('Meet & Greet notes');
  const canSubmit =
    targetValid && missing.length === 0 && !needsMngFirst && !mngUnverified && !mngBlockedReason && !transition.isPending;

  const handleSubmit = () => {
    if (!target || !canSubmit) return;
    const data: TransitionEnquiryData = { toStage: target, expectedFromStage: enquiry.stage };
    if (isOwned) {
      data.ownerUserId = ownerId === STAGE_DEFAULT ? null : ownerId;
      if (nextAction.trim()) data.nextAction = nextAction.trim();
      if (due) data.nextActionDueDate = due;
    }
    if (target === 'NOT_PROCEEDING') {
      data.closedReasonId = closedReasonId;
      if (closedDetail.trim()) data.closedReasonDetail = closedDetail.trim();
    }
    if (target === 'WAITLIST') {
      if (program.trim()) data.waitlistProgram = program.trim();
      if (preferredDay.trim()) data.waitlistPreferredDay = preferredDay.trim();
    }
    if (target === 'MNG_FOLLOW_UP') data.note = note.trim();

    setError(null);
    setChecklistBlocked(false);
    transition.mutate(
      { id: enquiry.id, data },
      {
        onSuccess: () => onClose(),
        onError: (e) => {
          const code = apiErrorCode(e);
          // Phase 6: the server names the student ("Finish Mia's onboarding checklist first."); the
          // way to the page is added under it, by the viewer's permission.
          setChecklistBlocked(code === 'ONBOARDING_CHECKLIST_INCOMPLETE');
          setError(
            code === 'ENQUIRY_STAGE_CHANGED'
              ? 'Someone else moved this enquiry. It has been reloaded: check where it is now and try again.'
              : code === 'ONBOARDING_CHECKLIST_INCOMPLETE'
                ? apiErrorMessage(e, 'Finish the onboarding checklist first.')
                : apiErrorMessage(e, 'Failed to move the stage'),
          );
        },
      },
    );
  };

  return (
    <>
      <Dialog open={open} onClose={transition.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {initialStage === 'ENROLLED' ? 'Mark enrolled' : 'Move stage'}
          <Typography variant="body2" color="text.secondary" component="div">
            Now: {stageLabel(config, enquiry.stage)}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {error && (
              <Alert severity="error">
                {error}
                {checklistBlocked && (
                  <Typography variant="body2" component="div" sx={{ mt: 0.5 }} data-testid="move-stage-checklist-hint">
                    {can('student_onboarding', 'read') ? (
                      <Link component={RouterLink} to={`/${schoolSlug}/admin/settling-in`}>
                        Open Onboarding check-in
                      </Link>
                    ) : (
                      'Someone with access to Onboarding check-in needs to tick the remaining items.'
                    )}
                  </Typography>
                )}
              </Alert>
            )}

            <FormControl fullWidth size="small" required>
              <InputLabel id="move-stage-target-label">Move to</InputLabel>
              <Select
                labelId="move-stage-target-label"
                label="Move to"
                value={targetValid ? target : ''}
                onChange={(e) => setTarget(e.target.value as EnquiryStage)}
              >
                {targets.map((s) => (
                  <MenuItem key={s.stage} value={s.stage}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(needsMngFirst || mngUnverified) && (
              <Alert severity="info">
                <Typography variant="body2" sx={{ mb: mayBookMng ? 1 : 0 }}>
                  {mngUnverified
                    ? 'The family has not confirmed the Meet & Greet booking yet. Book or link another to replace it, or wait for the family to confirm.'
                    : bookingMovesStage
                      ? `A Meet & Greet has to be booked first. The enquiry moves to ${stageLabel(config, 'MNG_BOOKED')} when it is.`
                      : `A Meet & Greet has to be booked first. From ${stageLabel(config, enquiry.stage)} booking it does not move the enquiry: book it, then move the stage here.`}
                </Typography>
                {mayBookMng ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button size="small" variant="outlined" onClick={() => openBooking('create')}>
                      Create a Meet & Greet
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => openBooking('link')}>
                      Link an existing Meet & Greet
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="body2">
                    Someone with Meet & Greet access must book or link it.
                  </Typography>
                )}
              </Alert>
            )}

            {mngBlockedReason && <Alert severity="warning">{mngBlockedReason}</Alert>}

            {(awaitingRefetch || bookingRecorded) && (
              <Alert severity="success">
                {awaitingRefetch
                  ? 'Meet & Greet recorded. Checking the enquiry...'
                  : `The Meet & Greet is recorded. The enquiry is still at ${stageLabel(config, enquiry.stage)}: the stage can now be moved.`}
              </Alert>
            )}

            {isOwned && (
              <>
                <FormControl fullWidth size="small" required={guardrail}>
                  <InputLabel id="move-stage-owner-label">Owner</InputLabel>
                  <Select
                    labelId="move-stage-owner-label"
                    label="Owner"
                    value={ownerOptions.some((o) => o.id === ownerId) ? ownerId : STAGE_DEFAULT}
                    onChange={(e) => setOwnerId(String(e.target.value))}
                  >
                    {/* With the guardrail on the server refuses "no person" (ENQUIRY_OWNER_REQUIRED), so
                        the placeholder is a prompt, not a choice. */}
                    <MenuItem value={STAGE_DEFAULT} disabled={guardrail}>
                      {guardrail ? 'Choose an owner' : 'Stage default'}
                    </MenuItem>
                    {ownerOptions.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Next action"
                  size="small"
                  fullWidth
                  required={guardrail}
                  value={nextAction}
                  onChange={(e) => setNextAction(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                />
                <TextField
                  label="Due date"
                  type="date"
                  size="small"
                  fullWidth
                  required={guardrail}
                  value={due}
                  onChange={(e) => setDue(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: minDue }}
                  error={duePast}
                  helperText={duePast ? 'The due date has passed.' : undefined}
                />
              </>
            )}

            {target === 'NOT_PROCEEDING' && (
              <>
                <EnquiryOptionSelect
                  kind="CLOSED_REASON"
                  label="Reason"
                  required
                  value={closedReasonId}
                  onChange={setClosedReasonId}
                />
                <TextField
                  label="Detail"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  required={needsDetail}
                  value={closedDetail}
                  onChange={(e) => setClosedDetail(e.target.value)}
                  inputProps={{ maxLength: 500 }}
                />
              </>
            )}

            {target === 'WAITLIST' && (
              <>
                <TextField
                  label="Program"
                  size="small"
                  fullWidth
                  value={program}
                  onChange={(e) => setProgram(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                />
                <TextField
                  label="Preferred day"
                  size="small"
                  fullWidth
                  value={preferredDay}
                  onChange={(e) => setPreferredDay(e.target.value)}
                  inputProps={{ maxLength: 100 }}
                />
              </>
            )}

            {target === 'MNG_FOLLOW_UP' && (
              <TextField
                label="Meet & Greet notes"
                size="small"
                fullWidth
                multiline
                minRows={3}
                required
                value={note}
                onChange={(e) => setNote(e.target.value)}
                inputProps={{ maxLength: 2000 }}
              />
            )}
          </Stack>
        </DialogContent>
        {missing.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ px: 3, pt: 1.5, textAlign: 'right' }}
            data-testid="move-stage-missing"
          >
            Still needed: {missing.join(', ')}
          </Typography>
        )}
        <DialogActions>
          <Button onClick={onClose} disabled={transition.isPending}>
            Cancel
          </Button>
          {!needsMngFirst && (
            <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
              {transition.isPending ? 'Moving...' : initialStage === 'ENROLLED' ? 'Mark enrolled' : 'Move stage'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {createOpen && (
        <ConvertToMeetAndGreetDialog open onClose={() => setCreateOpen(false)} onSuccess={handleBooked} enquiry={enquiry} />
      )}
      {linkOpen && (
        <LinkMeetAndGreetDialog enquiry={enquiry} open onClose={() => setLinkOpen(false)} onLinked={handleBooked} />
      )}
    </>
  );
}
